import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import IconButton from '@mui/material/IconButton';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiErrorText } from '../../api/errorText';
import type { MediaSourceSummary } from '../../api/types';
import RemoveVersionDialog from './RemoveVersionDialog';
import { describeVersion, fileName } from './versionText';

/// The files a title is played from, for the people who look after the shelf.
///
/// A version that was fetched from a discovery provider can be taken off again from here, and
/// another one fetched beside it. Files that came from a folder on a disk are listed for what
/// they are and left alone: removing those is a job for the folder, not for this page.
export default function ItemVersions({
  versions,
  removableBy,
  canRemove,
  canFetchAnother,
  onRemove,
  onFetchAnother,
}: {
  versions: MediaSourceSummary[];
  /// The discovery providers on this server. A version whose file one of them holds is one it
  /// can take back.
  removableBy: Set<string>;
  canRemove: boolean;
  canFetchAnother: boolean;
  onRemove: (version: MediaSourceSummary) => Promise<void>;
  onFetchAnother: () => void;
}) {
  const { t } = useTranslation();

  const [removing, setRemoving] = useState<MediaSourceSummary>();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();

  const removable = (version: MediaSourceSummary) =>
    canRemove && version.reference != null && removableBy.has(version.reference.providerId);

  // Nothing to do here for somebody who can neither take a version off nor fetch one.
  if (!canFetchAnother && !versions.some(removable)) {
    return null;
  }

  const confirm = async () => {
    if (!removing) {
      return;
    }

    setBusy(true);
    setProblem(undefined);

    try {
      await onRemove(removing);
    } catch (caught) {
      setProblem(apiErrorText(caught, t).message);
    } finally {
      setBusy(false);
      setRemoving(undefined);
    }
  };

  return (
    <Stack spacing={1.5} sx={{ maxWidth: 720 }}>
      <Stack direction="row" spacing={2} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="h3" sx={{ flexGrow: 1 }}>
          {t('versions.title')}
        </Typography>

        {canFetchAnother && (
          <Button variant="outlined" startIcon={<LibraryAddIcon />} onClick={onFetchAnother}>
            {t('discover.fetchAnother')}
          </Button>
        )}
      </Stack>

      {problem && <Alert severity="error">{problem}</Alert>}

      <List dense disablePadding>
        {versions.map((version) => (
          <ListItem
            key={version.id}
            disableGutters
            secondaryAction={
              removable(version) &&
              (busy && removing?.id === version.id ? (
                <CircularProgress size={18} />
              ) : (
                <IconButton
                  edge="end"
                  disabled={busy}
                  aria-label={t('versions.remove.action')}
                  onClick={() => setRemoving(version)}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              ))
            }
          >
            <ListItemText
              primary={describeVersion(version, t)}
              secondary={fileName(version.path)}
              slotProps={{ secondary: { sx: { overflowWrap: 'anywhere' } } }}
            />
          </ListItem>
        ))}
      </List>

      <RemoveVersionDialog
        name={removing ? fileName(removing.path) : undefined}
        detail={removing ? describeVersion(removing, t) : undefined}
        busy={busy}
        onCancel={() => setRemoving(undefined)}
        onConfirm={() => void confirm()}
      />
    </Stack>
  );
}
